import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import {
  Container as ContainerBase,
  ContentWithPaddingXl,
} from "components/misc/Layouts";
import { SectionDescription } from "components/misc/Typography";

const Container = styled(ContainerBase)`
  ${tw`my-8 lg:my-10 -mx-8 px-8 text-white font-[CustomFont]`}
  background: linear-gradient(135deg, #C78A41 0%, #9f6e33 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="1.5" fill="rgba(255,255,255,0.1)"/></svg>');
    opacity: 0.3;
  }
`;

const HeadingContainer = tw.div`relative z-10`;
const Heading = tw(SectionHeading)`
  sm:text-4xl md:text-5xl text-center
`;

const Subheading = tw(SubheadingBase)`
  text-white text-center
`;

const Description = tw(SectionDescription)`
  text-white text-center mx-auto max-w-screen-md
`;

const StatsContainer = tw.div`
  mt-8 flex flex-col sm:flex-row items-center justify-center flex-wrap max-w-screen-md justify-between mx-auto relative z-10
`;

const Stat = styled.div`
  ${tw`flex flex-col text-center p-4 tracking-wide`}
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }
`;

const StatKey = tw.div`
  text-2xl font-medium
`;

const StatValue = styled.div`
  ${tw`text-5xl font-black`}
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
`;

const AnimatedStatValue = ({ end }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inView) {
      const numericEnd = parseInt(end.replace(/[^0-9]/g, ""), 10);
      if (isNaN(numericEnd)) return;

      let start = 0;
      const duration = 2000;
      const frameRate = 60;
      const totalFrames = Math.round((duration / 1000) * frameRate);
      let frame = 0;

      const counter = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        start = Math.round(numericEnd * progress);

        if (frame === totalFrames) {
          clearInterval(counter);
          setCount(numericEnd);
        } else {
          setCount(start);
        }
      }, 1000 / frameRate);

      return () => clearInterval(counter);
    }
  }, [inView, end]);

  const suffix = end.replace(/[0-9]/g, "");

  return (
    <StatValue ref={ref} style={{ fontSize: "3.5rem" }}>
      {count}
      {suffix}
    </StatValue>
  );
};

export default ({
  subheading = "",
  heading = "Over 9000 Projects Completed",
  description = "",
  stats = [
    {
      key: "Clients",
      value: "00+",
    },
    {
      key: "Revenue",
      value: "$100M+",
    },
    {
      key: "Employees",
      value: "0+",
    },
  ],
}) => {
  return (
    <Container dir="rtl">
      <ContentWithPaddingXl>
        <HeadingContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
        </HeadingContainer>
        <StatsContainer>
          {stats.map((stat, index) => (
            <Stat key={index}>
              <AnimatedStatValue end={stat.value} />
              <StatKey style={{ fontSize: "1.75rem" }}>{stat.key}</StatKey>
            </Stat>
          ))}
        </StatsContainer>
      </ContentWithPaddingXl>
    </Container>
  );
};
